import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  Receipt,
  FolderOpen,
  Settings,
  ShieldCheck,
  LogOut,
  Sun,
  Moon,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Inbox,
  MessagesSquare,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { useActiveClinic } from '@/lib/ActiveClinicContext';
import { useClinic } from '@/hooks/useClinicSettings';
import { ClinicSelector } from '@/components/ClinicSelector';
import { Logo } from '@/components/Logo';
import { cn } from '@/utils/cn';
import {
  ECONOMY_PERMISSION_RESOURCES,
  type PermissionResource,
} from '@/lib/permissions';
import {
  isClinicModuleEnabled,
  readActivatedModules,
} from '@/lib/clinicModules';

const STORAGE_KEY = 'clinio-v2-sidebar-collapsed';

interface NavItem {
  to: string;
  i18nKey: string;
  icon: typeof LayoutDashboard;
}

interface NavItemDef extends NavItem {
  /** Optional `activated_modules` flag that must be true. */
  moduleKey?: string;
  /** Show only when current user is a platform admin. */
  platformAdminOnly?: boolean;
  /** RBAC resource required for view access. */
  permissionResource?: PermissionResource;
}

const NAV_ITEMS: NavItemDef[] = [
  { to: '/dashboard', i18nKey: 'nav.dashboard', icon: LayoutDashboard },
  {
    to: '/patients',
    i18nKey: 'nav.patients',
    icon: Users,
    moduleKey: 'patients',
    permissionResource: 'patients',
  },
  {
    to: '/henvisninger',
    i18nKey: 'nav.referrals',
    icon: Inbox,
    moduleKey: 'referrals',
    permissionResource: 'referrals',
  },
  {
    to: '/care-communications',
    i18nKey: 'nav.communicate',
    icon: MessagesSquare,
    moduleKey: 'communicate',
    permissionResource: 'patients',
  },
  {
    to: '/appointments',
    i18nKey: 'nav.appointments',
    icon: CalendarDays,
    moduleKey: 'appointments',
    permissionResource: 'appointments',
  },
  {
    to: '/documents',
    i18nKey: 'nav.documents',
    icon: FolderOpen,
    moduleKey: 'documents',
    permissionResource: 'documents',
  },
  {
    to: '/economy/invoices',
    i18nKey: 'nav.economy',
    icon: Receipt,
    moduleKey: 'invoices',
    // Economy hub: invoices / payments / quotes (any view)
    permissionResource: 'invoices',
  },
  { to: '/admin', i18nKey: 'nav.admin', icon: ShieldCheck, platformAdminOnly: true },
  { to: '/help', i18nKey: 'nav.help', icon: HelpCircle },
  { to: '/settings', i18nKey: 'nav.settings', icon: Settings, permissionResource: 'settings_general' },
];

export function Sidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, user, isPlatformAdmin, hasPermission } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { activeClinic } = useActiveClinic();
  const activeClinicSettings = useClinic(activeClinic?.id ?? '');

  const activatedModules = readActivatedModules(
    activeClinicSettings.data?.activated_modules,
  );

  const visibleNavItems = NAV_ITEMS.filter((item) => {
    if (item.platformAdminOnly && !isPlatformAdmin) return false;
    if (activeClinic?.id) {
      if (item.to === '/economy/invoices') {
        const canEconomy = ECONOMY_PERMISSION_RESOURCES.some((r) =>
          hasPermission(activeClinic.id, r, 'view'),
        );
        if (!canEconomy) return false;
      } else if (item.permissionResource) {
        if (!hasPermission(activeClinic.id, item.permissionResource, 'view')) return false;
      }
    }
    if (item.moduleKey) {
      return isClinicModuleEnabled(activatedModules, item.moduleKey);
    }
    return true;
  });

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      // ignore
    }
  }, [collapsed]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      navigate('/sign-in', { replace: true });
    }
  };

  return (
    <aside
      className={cn(
        'flex h-screen flex-col border-r border-gray-200 bg-card-light transition-[width] duration-200 dark:border-gray-700 dark:bg-card-dark',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <div className="flex items-center justify-between border-b border-gray-200 p-3 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Logo size={32} />
          {!collapsed && (
            <span className="text-lg font-semibold text-gray-900 dark:text-white">Clinio</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      {/* Active clinic selector */}
      {!collapsed && (
        <div className="border-b border-gray-200 px-2 py-3 dark:border-gray-700">
          <ClinicSelector />
        </div>
      )}

      <nav className="flex-1 overflow-y-auto p-2">
        <ul className="space-y-1">
          {visibleNavItems.map(({ to, i18nKey, icon: Icon }) => (
            <li key={to}>
              <NavLink
                to={to}
                className={({ isActive }) => {
                  const active =
                    to.startsWith('/economy')
                      ? location.pathname.startsWith('/economy')
                      : isActive;
                  return cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition',
                    active
                      ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
                  );
                }}
                title={t(i18nKey)}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!collapsed && <span className="truncate">{t(i18nKey)}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="border-t border-gray-200 p-2 dark:border-gray-700">
        {!collapsed && user?.email && (
          <div className="truncate px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
            {user.email}
            {isPlatformAdmin && (
              <span className="ml-2 inline-flex rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                Admin
              </span>
            )}
          </div>
        )}

        <NavLink
          to="/account/security"
          className={({ isActive }) =>
            cn(
              'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition',
              isActive
                ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
            )
          }
          title={t('nav.account_security')}
        >
          <ShieldCheck className="h-5 w-5" />
          {!collapsed && <span>{t('nav.account_security')}</span>}
        </NavLink>

        <button
          type="button"
          onClick={toggleTheme}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          title={theme === 'dark' ? t('nav.toggle_theme_light') : t('nav.toggle_theme_dark')}
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          {!collapsed && (
            <span>
              {theme === 'dark' ? t('nav.toggle_theme_light') : t('nav.toggle_theme_dark')}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={handleSignOut}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          title={t('nav.sign_out')}
        >
          <LogOut className="h-5 w-5" />
          {!collapsed && <span>{t('nav.sign_out')}</span>}
        </button>
      </div>
    </aside>
  );
}
